import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import Navbar from '@/components/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import {
  FileText, ShieldCheck, Send, CheckCircle2, ArrowRight, Download,
  Building2, AlertCircle, Loader2, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* =========================
   STEP INDICATOR
   ========================= */
function StepIndicator({ step, t }) {
  const steps = [
    { key: 'review', label: t('ct_step_review'), icon: FileText },
    { key: 'accept', label: t('ct_step_accept'), icon: ShieldCheck },
    { key: 'otp',    label: t('ct_step_otp'),    icon: Send },
    { key: 'signed', label: t('ct_step_signed'), icon: CheckCircle2 },
  ];
  const activeIdx = steps.findIndex(s => s.key === step);
  return (
    <div className="flex items-center justify-center gap-1.5 sm:gap-3 mb-8 flex-wrap" data-testid="ct-stepper">
      {steps.map((s, i) => {
        const Icon = s.icon;
        const isActive = i === activeIdx;
        const isDone = i < activeIdx;
        return (
          <React.Fragment key={s.key}>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-sans font-medium border transition-colors ${
              isDone ? 'bg-primary/10 text-primary border-primary/30' :
              isActive ? 'bg-primary text-white border-primary shadow-sm' :
              'bg-card text-muted-foreground border-border'
            }`} data-testid={`ct-step-${s.key}`}>
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="hidden sm:inline">{s.label}</span>
            </div>
            {i < steps.length - 1 && <ArrowRight className="w-3 h-3 text-muted-foreground" strokeWidth={1.5} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* =========================
   BUSINESS DATA VIEWER (readonly)
   ========================= */
function BusinessDataPanel({ preview, t, onEditProfile }) {
  const rows = [
    ['ct_field_company', preview.company_name],
    ['ct_field_vat', preview.vat_number],
    ['ct_field_fiscal', preview.company_fiscal_code || preview.vat_number],
    ['ct_field_address', preview.registered_address],
    ['ct_field_email', preview.email],
    ['ct_field_phone', preview.phone_number],
    ['ct_field_iban', preview.iban_masked],
    ['ct_field_legal_rep', `${preview.legal_rep_first_name || ''} ${preview.legal_rep_last_name || ''}`.trim()],
    ['ct_field_legal_rep_fc', preview.legal_rep_fiscal_code],
  ];
  return (
    <Card className="border-border rounded-2xl mb-6" data-testid="ct-business-panel">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <h3 className="font-sans font-semibold text-base">{t('ct_business_data')}</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onEditProfile} className="text-xs rounded-full" data-testid="ct-edit-profile-btn">
            {t('ct_edit_profile')}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground font-sans mb-4">{t('ct_data_readonly')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm font-sans">
          {rows.map(([k, v]) => (
            <div key={k} className="flex flex-col py-1 border-b border-border/50 last:border-0">
              <span className="text-[11px] text-muted-foreground uppercase tracking-wide">{t(k)}</span>
              <span className="text-sm text-foreground font-medium">{v || '—'}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* =========================
   CONTRACT VIEWER
   ========================= */
function ContractTextPanel({ template, t, lang }) {
  const showEn = lang === 'en' && template.content_en;
  return (
    <Card className="border-border rounded-2xl mb-6" data-testid="ct-text-panel">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-sans font-semibold text-base flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" strokeWidth={1.5} />
            {t('ct_contract_text')}
          </h3>
          <div className="text-xs text-muted-foreground font-sans">
            {t('ct_version')}: <span className="font-mono">{template.version}</span>
          </div>
        </div>
        <div
          className="max-h-96 overflow-y-auto pr-2 text-sm font-sans leading-relaxed whitespace-pre-wrap text-foreground/90 border border-border rounded-lg p-4 bg-muted/20"
          data-testid="ct-contract-scroll"
        >
          {showEn ? template.content_en : template.content}
        </div>
      </CardContent>
    </Card>
  );
}

/* =========================
   MAIN CONTRACT PAGE
   ========================= */
export default function ContractPage() {
  const { user, checkAuth } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const [step, setStep] = useState('review'); // review | accept | otp | signed
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [signing, setSigning] = useState(false);
  const [template, setTemplate] = useState(null);
  const [preview, setPreview] = useState(null);
  const [profileComplete, setProfileComplete] = useState(false);
  const [accountStatus, setAccountStatus] = useState('');

  // Terms
  const [terms1, setTerms1] = useState(false);
  const [terms2, setTerms2] = useState(false);

  // Acceptance
  const [acceptanceId, setAcceptanceId] = useState(null);
  const [contractNumber, setContractNumber] = useState(null);
  const [signedAt, setSignedAt] = useState(null);

  // OTP
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const submitOnceRef = useRef(false);

  // Load active template on mount
  useEffect(() => {
    (async () => {
      try {
        // If user already signed, jump straight to success and load their existing acceptance
        const meRes = await axios.get(`${API}/contracts/acceptances/me`, { withCredentials: true });
        const signedItem = (meRes.data.items || []).find(i => i.status === 'signed');
        if (signedItem) {
          setAcceptanceId(signedItem.acceptance_id);
          setContractNumber(signedItem.contract_number);
          setSignedAt(signedItem.signed_at);
          setStep('signed');
          setLoading(false);
          return;
        }
        const res = await axios.get(`${API}/contracts/active-template`, { withCredentials: true });
        setTemplate(res.data.template);
        setPreview(res.data.preview);
        setProfileComplete(res.data.profile_complete);
        setAccountStatus(res.data.account_status);
        // Resume pending acceptance if exists
        const pending = (meRes.data.items || []).find(i => i.status === 'otp_pending' || i.status === 'signing');
        if (pending) {
          setAcceptanceId(pending.acceptance_id);
          setContractNumber(pending.contract_number);
          setStep('otp');
        }
      } catch (e) {
        const status = e.response?.status;
        if (status === 401) {
          // Session expired or invalid: clear stale sessionStorage and go to login
          try { sessionStorage.removeItem('womio_auth_user'); } catch {}
          navigate('/restaurant-auth');
          return;
        }
        if (status === 403) { navigate('/'); return; }
        // Network / 404 / 5xx — do NOT show the misleading "profile incomplete" warning
        setLoadError(
          status === 404
            ? 'Servizio contratti temporaneamente non disponibile. Ricarica la pagina.'
            : (e.response?.data?.detail || t('ct_error_generic'))
        );
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resend countdown tick
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const id = setTimeout(() => setResendCountdown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(id);
  }, [resendCountdown]);

  const canProceedToOtp = terms1 && terms2 && profileComplete && !submitting;

  const handleAccept = async () => {
    if (!canProceedToOtp || !template) return;
    if (submitOnceRef.current) return;
    submitOnceRef.current = true;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contracts/acceptances`, {
        template_id: template.template_id,
        terms_accepted: true,
        representative_declared: true,
      }, { withCredentials: true });
      setAcceptanceId(res.data.acceptance_id);
      setContractNumber(res.data.contract_number);
      setStep('otp');
      // Immediately send OTP
      await handleSendOtp(res.data.acceptance_id, true);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('ct_error_generic'));
      submitOnceRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendOtp = async (accId, silent = false) => {
    const id = accId || acceptanceId;
    if (!id) return;
    setOtpSending(true);
    try {
      await axios.post(`${API}/contracts/acceptances/${id}/otp/send`, { lang }, { withCredentials: true });
      setResendCountdown(60);
      if (!silent) toast.success('Codice inviato');
    } catch (e) {
      toast.error(e.response?.data?.detail || t('ct_error_generic'));
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!acceptanceId || otpCode.length !== 6 || signing) return;
    setSigning(true);
    try {
      const res = await axios.post(
        `${API}/contracts/acceptances/${acceptanceId}/otp/verify`,
        { code: otpCode },
        { withCredentials: true }
      );
      if (res.data.status === 'signed') {
        setContractNumber(res.data.contract_number);
        setSignedAt(new Date().toISOString());
        setStep('signed');
        // Refresh user auth to pick up new account_status='active'
        await checkAuth();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('ct_error_generic'));
      setOtpCode('');
    } finally {
      setSigning(false);
    }
  };

  const downloadPdf = async () => {
    if (!acceptanceId) return;
    try {
      const res = await axios.get(`${API}/contracts/acceptances/${acceptanceId}/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `WOMIO_${contractNumber || acceptanceId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('ct_error_generic'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="contract-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-20 sm:pt-24">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-sans font-medium mb-3">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t('ct_page_title')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="ct-title">
            {t('ct_page_title')}
          </h1>
          {step !== 'signed' && (
            <p className="text-muted-foreground text-sm sm:text-base font-sans mt-2 max-w-2xl mx-auto">{t('ct_subtitle')}</p>
          )}
        </div>

        <StepIndicator step={step} t={t} />

        {/* Load error banner (network/404/5xx) — shown INSTEAD of misleading "profile incomplete" */}
        {loadError && (
          <div className="mb-6 flex items-start gap-3 p-4 rounded-xl border border-destructive/30 bg-destructive/5 text-destructive" data-testid="ct-load-error">
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="text-sm font-sans flex-1">
              <p className="font-semibold">{loadError}</p>
              <Button variant="link" onClick={() => window.location.reload()} className="p-0 h-auto text-destructive underline" data-testid="ct-reload-btn">
                Ricarica ora
              </Button>
            </div>
          </div>
        )}

        {/* PENDING banner */}
        {!loadError && accountStatus === 'pending_contract' && step !== 'signed' && (
          <div className="mb-6 flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900" data-testid="ct-pending-banner">
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.5} />
            <p className="text-sm font-sans">{t('ct_pending_banner')}</p>
          </div>
        )}

        {/* Profile incomplete warning — only when API responded successfully AND profile really is incomplete */}
        {!loadError && template && !profileComplete && step !== 'signed' && (
          <div className="mb-6 flex items-start gap-3 p-4 rounded-xl border border-destructive/30 bg-destructive/5 text-destructive" data-testid="ct-profile-warn">
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div className="text-sm font-sans">
              <p className="font-semibold">{t('ct_profile_incomplete')}</p>
              <Button variant="link" onClick={() => navigate('/restaurant/profile')} className="p-0 h-auto text-destructive underline" data-testid="ct-goto-profile">
                {t('ct_edit_profile')}
              </Button>
            </div>
          </div>
        )}

        {/* STEP: REVIEW + ACCEPT (combined page) */}
        {(step === 'review' || step === 'accept') && preview && template && (
          <>
            <BusinessDataPanel preview={preview} t={t} onEditProfile={() => navigate('/restaurant/profile')} />
            <ContractTextPanel template={template} t={t} lang={lang} />
            <Card className="border-border rounded-2xl mb-6" data-testid="ct-accept-panel">
              <CardContent className="p-5 sm:p-6 space-y-4">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="ct-terms1-label">
                  <Checkbox checked={terms1} onCheckedChange={setTerms1} className="mt-0.5" data-testid="ct-terms1" />
                  <span className="text-sm font-sans leading-relaxed">{t('ct_terms_1')}</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="ct-terms2-label">
                  <Checkbox checked={terms2} onCheckedChange={setTerms2} className="mt-0.5" data-testid="ct-terms2" />
                  <span className="text-sm font-sans leading-relaxed">{t('ct_terms_2')}</span>
                </label>
                <Button
                  onClick={handleAccept}
                  disabled={!canProceedToOtp}
                  className="w-full rounded-full h-12 text-base btn-lift"
                  data-testid="ct-proceed-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                  {t('ct_proceed')}
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        {/* STEP: OTP */}
        {step === 'otp' && (
          <Card className="border-border rounded-2xl max-w-md mx-auto" data-testid="ct-otp-panel">
            <CardContent className="p-6 sm:p-8 text-center">
              {signing ? (
                <div className="py-8">
                  <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                  <p className="text-base font-sans font-medium" data-testid="ct-signing-msg">{t('ct_signing')}</p>
                </div>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-xl font-semibold font-sans mb-1">{t('ct_otp_title')}</h2>
                  <p className="text-sm text-muted-foreground font-sans mb-1">{t('ct_otp_desc')}</p>
                  <p className="text-sm font-mono font-semibold text-foreground mb-4" data-testid="ct-otp-email">{preview?.email || user?.email || '—'}</p>
                  <div className="flex justify-center mb-3">
                    <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode} data-testid="ct-otp-input">
                      <InputOTPGroup>
                        {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} />)}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <p className="text-xs text-muted-foreground font-sans mb-4">{t('ct_otp_validity')}</p>
                  <Button
                    onClick={handleVerifyOtp}
                    disabled={otpCode.length !== 6 || signing}
                    className="w-full rounded-full h-11 btn-lift mb-3"
                    data-testid="ct-verify-btn"
                  >
                    <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {t('ct_otp_verify')}
                  </Button>
                  <div className="flex items-center justify-between text-xs font-sans">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleSendOtp()}
                      disabled={otpSending || resendCountdown > 0}
                      className="text-primary"
                      data-testid="ct-resend-btn"
                    >
                      {resendCountdown > 0
                        ? t('ct_otp_resend_wait').replace('{s}', resendCountdown)
                        : t('ct_otp_resend')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/restaurant/profile')} className="text-muted-foreground text-[11px]" data-testid="ct-change-email-btn">
                      {t('ct_otp_change_email')}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* STEP: SIGNED */}
        {step === 'signed' && (
          <Card className="border-primary/30 rounded-2xl max-w-lg mx-auto bg-gradient-to-br from-primary/5 to-transparent" data-testid="ct-success-panel">
            <CardContent className="p-6 sm:p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-9 h-9 text-primary" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="ct-success-title">
                {t('ct_success_title')}
              </h2>
              <p className="text-sm text-muted-foreground font-sans mb-5">{t('ct_success_desc')}</p>
              <div className="bg-white rounded-xl border border-border p-4 mb-5 text-left text-sm font-sans" data-testid="ct-success-details">
                <div className="flex justify-between py-1.5 border-b border-border/60">
                  <span className="text-muted-foreground">{t('ct_success_number')}</span>
                  <span className="font-mono font-semibold text-primary">{contractNumber}</span>
                </div>
                {signedAt && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-muted-foreground">{t('ct_success_signed')}</span>
                    <span>{new Date(signedAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={downloadPdf} className="flex-1 rounded-full" data-testid="ct-download-btn">
                  <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {t('ct_download_pdf')}
                </Button>
                <Button onClick={async () => { try { await checkAuth(); } catch(_) {} navigate('/restaurant/dashboard', { replace: true }); }} className="flex-1 rounded-full btn-lift" data-testid="ct-goto-dashboard">
                  {t('ct_go_dashboard')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
